import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import Navigation from "@/components/Navigation";
import TopHeader from "@/components/TopHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { MessageCircle, Search, ChevronRight, UserPlus, Check, X, Bell } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";

const Messages = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("messages"); // "messages" or "requests"

  useEffect(() => {
    fetchConversations();
    fetchPendingRequests();
  }, []);

  const fetchPendingRequests = async () => {
    try {
      const response = await axios.get(`${API}/connections/requests`, { withCredentials: true });
      setPendingRequests(response.data);
    } catch (error) {
      console.error("Fetch pending requests error:", error);
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      await axios.post(`${API}/connections/accept/${requestId}`, {}, { withCredentials: true });
      toast.success("Connection accepted!");
      fetchPendingRequests();
      fetchConversations();
    } catch (error) {
      toast.error("Failed to accept request");
    }
  };

  const handleDeclineRequest = async (requestId) => {
    try {
      await axios.post(`${API}/connections/decline/${requestId}`, {}, { withCredentials: true });
      toast.success("Request declined");
      fetchPendingRequests();
    } catch (error) {
      toast.error("Failed to decline request");
    }
  };

  const fetchConversations = async () => {
    try {
      // Get all accepted connections (matches)
      const response = await axios.get(`${API}/connections/matches`, { withCredentials: true });
      
      // For each connection, get the last message
      const conversationsWithLastMessage = await Promise.all(
        response.data.map(async (connection) => {
          try {
            const messagesRes = await axios.get(
              `${API}/chat/${connection.request_id}/messages`, 
              { withCredentials: true }
            );
            const messages = messagesRes.data;
            const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
            
            return {
              ...connection,
              lastMessage,
              unreadCount: 0 // Could be implemented with read receipts
            };
          } catch (error) {
            return {
              ...connection,
              lastMessage: null,
              unreadCount: 0
            };
          }
        })
      );
      
      // Sort by last message time (most recent first)
      conversationsWithLastMessage.sort((a, b) => {
        const timeA = a.lastMessage ? new Date(a.lastMessage.created_at) : new Date(a.created_at || 0);
        const timeB = b.lastMessage ? new Date(b.lastMessage.created_at) : new Date(b.created_at || 0);
        return timeB - timeA;
      });
      
      setConversations(conversationsWithLastMessage);
    } catch (error) {
      console.error("Fetch conversations error:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredConversations = conversations.filter(conv => 
    conv.connected_user?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-cream pb-24">
      <Navigation />
      <TopHeader title="Messages" />
      
      {/* Tabs */}
      <div className="px-6 py-3 bg-white/50 border-b border-sage/10">
        <div className="max-w-2xl mx-auto flex gap-2">
          <Button
            variant={activeTab === "messages" ? "default" : "outline"}
            onClick={() => setActiveTab("messages")}
            className={`flex-1 rounded-full ${activeTab === "messages" ? "bg-sage text-white" : "border-sage/30"}`}
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            Chats
          </Button>
          <Button
            variant={activeTab === "requests" ? "default" : "outline"}
            onClick={() => setActiveTab("requests")}
            className={`flex-1 rounded-full relative ${activeTab === "requests" ? "bg-sage text-white" : "border-sage/30"}`}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Requests
            {pendingRequests.length > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {pendingRequests.length}
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="px-6 py-4 bg-white/50">
        <div className="max-w-2xl mx-auto">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={activeTab === "messages" ? "Search conversations..." : "Search requests..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-11 bg-white border-sage/20 rounded-full"
            />
          </div>
        </div>
      </div>

      {/* Content based on active tab */}
      <main className="px-6 py-4">
        <div className="max-w-2xl mx-auto space-y-2">
          {activeTab === "requests" ? (
            // Pending Requests Tab
            <>
              {pendingRequests.length === 0 ? (
                <Card className="bg-white border-sage/20">
                  <CardContent className="p-8 text-center">
                    <UserPlus className="w-16 h-16 text-sage/30 mx-auto mb-4" />
                    <h3 className="text-lg font-serif text-sage-darker mb-2">No pending requests</h3>
                    <p className="text-muted-foreground text-sm">
                      When someone wants to connect with you, their request will appear here.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                pendingRequests
                  .filter(req => req.from_user?.name?.toLowerCase().includes(searchQuery.toLowerCase()))
                  .map((request) => (
                    <Card key={request.request_id} className="bg-white border-sage/20">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-14 h-14 border-2 border-sage/20">
                            <AvatarImage src={request.from_user?.picture} />
                            <AvatarFallback className="bg-sage/20 text-sage-dark text-lg">
                              {request.from_user?.name?.[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sage-darker font-medium truncate">
                              {request.from_user?.name}
                            </h3>
                            <p className="text-sm text-muted-foreground truncate">
                              {request.from_user?.phase && (
                                <span>
                                  {request.from_user.phase === 'ttc' ? 'Trying to Conceive' : 
                                   request.from_user.phase === 'pregnant' ? 'Pregnant' : 'Postpartum'}
                                </span>
                              )}
                              {request.from_user?.location && ` • ${request.from_user.location}`}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="icon"
                              onClick={() => handleAcceptRequest(request.request_id)}
                              className="bg-sage text-white hover:bg-sage-dark rounded-full h-10 w-10"
                            >
                              <Check className="w-5 h-5" />
                            </Button>
                            <Button
                              size="icon"
                              variant="outline"
                              onClick={() => handleDeclineRequest(request.request_id)}
                              className="border-red-200 text-red-500 hover:bg-red-50 rounded-full h-10 w-10"
                            >
                              <X className="w-5 h-5" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
              )}
            </>
          ) : (
            // Messages Tab
            <>
              {loading ? (
            // Loading skeleton
            <div className="space-y-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-full bg-sage/10" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-sage/10 rounded w-1/3" />
                      <div className="h-3 bg-sage/10 rounded w-2/3" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredConversations.length === 0 ? (
            <Card className="bg-white border-sage/20">
              <CardContent className="p-8 text-center">
                <MessageCircle className="w-16 h-16 text-sage/30 mx-auto mb-4" />
                <h3 className="text-lg font-serif text-sage-darker mb-2">No conversations yet</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  {searchQuery ? "No conversations match your search" : "Match with someone to start chatting!"}
                </p>
                {!searchQuery && (
                  <Link 
                    to="/discover" 
                    className="text-sage hover:text-sage-dark font-medium inline-flex items-center gap-1"
                  >
                    Discover people <ChevronRight className="w-4 h-4" />
                  </Link>
                )}
              </CardContent>
            </Card>
          ) : (
            filteredConversations.map((conversation) => {
              const otherUser = conversation.connected_user;
              const lastMessage = conversation.lastMessage;
              const isOwnMessage = lastMessage?.sender_id === user?.user_id;
              
              return (
                <Link 
                  key={conversation.request_id} 
                  to={`/chat/${conversation.request_id}`}
                  className="block"
                >
                  <Card className="bg-white border-sage/20 hover:shadow-md hover:border-sage/40 transition-all">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        {/* Avatar */}
                        <div className="relative">
                          <Avatar className="w-14 h-14 border-2 border-sage/20">
                            <AvatarImage src={otherUser?.picture} />
                            <AvatarFallback className="bg-sage/20 text-sage-dark text-lg">
                              {otherUser?.name?.[0]}
                            </AvatarFallback>
                          </Avatar>
                          {/* Online indicator could go here */}
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <h3 className="text-sage-darker font-medium truncate">
                              {otherUser?.name}
                            </h3>
                            {lastMessage && (
                              <span className="text-xs text-muted-foreground flex-shrink-0">
                                {formatDistanceToNow(new Date(lastMessage.created_at), { addSuffix: false })}
                              </span>
                            )}
                          </div>
                          
                          <p className="text-sm text-muted-foreground truncate">
                            {lastMessage ? (
                              <>
                                {isOwnMessage && <span className="text-sage-dark">You: </span>}
                                {lastMessage.content}
                              </>
                            ) : (
                              <span className="italic">Start a conversation</span>
                            )}
                          </p>
                          
                          {/* Phase badge */}
                          {otherUser?.phase && (
                            <Badge 
                              variant="outline" 
                              className="mt-2 text-xs border-sage/20 text-sage-dark"
                            >
                              {otherUser.phase === 'ttc' ? 'Trying to Conceive' : 
                               otherUser.phase === 'pregnant' ? 'Pregnant' : 
                               otherUser.phase === 'postpartum' ? 'Postpartum' : otherUser.phase}
                            </Badge>
                          )}
                        </div>
                        
                        {/* Unread indicator */}
                        {conversation.unreadCount > 0 && (
                          <div className="w-6 h-6 rounded-full bg-sage text-white text-xs flex items-center justify-center">
                            {conversation.unreadCount}
                          </div>
                        )}
                        
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })
          )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default Messages;
